/** 記事本文だけから目次を生成し、Shopifyのタグ一覧から関連記事を補完する。 */
class FarmageBlogArticle extends HTMLElement {
  private controller: AbortController | null = null;

  connectedCallback(): void {
    this.controller?.abort();
    this.controller = new AbortController();
    this.buildTableOfContents();
    this.wrapTables();
    this.initCopyButton(this.controller.signal);
    void this.loadRelatedArticles(this.controller.signal);
  }

  disconnectedCallback(): void {
    this.controller?.abort();
  }

  private buildTableOfContents(): void {
    const content = this.querySelector<HTMLElement>("[data-article-content]");
    const toc = this.querySelector<HTMLElement>("[data-article-toc]");
    const list = this.querySelector<HTMLUListElement>(
      "[data-article-toc-list]",
    );
    if (!content || !toc || !list) return;
    list.replaceChildren();
    const headings = content.querySelectorAll<HTMLElement>("h2, h3");
    for (const [index, heading] of Array.from(headings).entries()) {
      const text = heading.textContent?.trim();
      if (!text) continue;
      // 移行本文の既存アンカーを保持。重複IDや空IDだけを補正する。
      const matchingIds = heading.id
        ? Array.from(document.querySelectorAll<HTMLElement>("[id]")).filter(
            (element) => element.id === heading.id,
          )
        : [];
      if (!heading.id || matchingIds[0] !== heading) {
        const base = `Article-${this.dataset.sectionId}-${index + 1}`;
        let id = base;
        let suffix = 1;
        while (document.getElementById(id)) id = `${base}-${suffix++}`;
        heading.id = id;
      }
      heading.tabIndex = -1;
      const item = document.createElement("li");
      if (heading.tagName === "H3")
        item.className = "p-blog-article__toc-subitem";
      const link = document.createElement("a");
      link.href = `#${encodeURIComponent(heading.id)}`;
      link.textContent = text;
      item.append(link);
      list.append(item);
    }
    toc.hidden = list.children.length === 0;
  }

  private wrapTables(): void {
    for (const table of Array.from(
      this.querySelectorAll<HTMLTableElement>("[data-article-content] table"),
    )) {
      if (
        table.parentElement?.classList.contains("p-blog-article__table-wrapper")
      )
        continue;
      const wrapper = document.createElement("div");
      wrapper.className = "p-blog-article__table-wrapper";
      wrapper.tabIndex = 0;
      wrapper.setAttribute("role", "region");
      wrapper.setAttribute(
        "aria-label",
        table.caption?.textContent?.trim() || "表",
      );
      table.before(wrapper);
      wrapper.append(table);
    }
  }

  private initCopyButton(signal: AbortSignal): void {
    const button = this.querySelector<HTMLButtonElement>("[data-copy-url]");
    const status = this.querySelector<HTMLElement>("[data-copy-status]");
    const fallback = this.querySelector<HTMLElement>("[data-copy-fallback]");
    const input = fallback?.querySelector<HTMLInputElement>("input");
    const url = this.dataset.shareUrl;
    if (!button || !url) return;
    button.hidden = false;
    button.addEventListener(
      "click",
      async () => {
        try {
          if (!navigator.clipboard?.writeText)
            throw new Error("Clipboard unavailable");
          await navigator.clipboard.writeText(url);
          if (signal.aborted) return;
          if (status) status.textContent = "URLをコピーしました。";
          if (fallback) fallback.hidden = true;
        } catch {
          if (signal.aborted) return;
          if (status)
            status.textContent = "記事URLを選択してコピーしてください。";
          if (fallback && input) {
            fallback.hidden = false;
            input.focus();
            input.select();
          }
        }
      },
      { signal },
    );
  }

  private async loadRelatedArticles(signal: AbortSignal): Promise<void> {
    const source = this.querySelector<HTMLAnchorElement>(
      "[data-related-source] a",
    );
    const list = this.querySelector<HTMLUListElement>("[data-related-list]");
    const aside = this.querySelector<HTMLElement>("[data-article-related]");
    if (!source || !list || !aside) return;
    const url = new URL(source.href);
    if (url.origin !== location.origin) return;
    url.searchParams.set("view", "related-articles");
    url.searchParams.delete("page");
    const previewTheme = new URL(location.href).searchParams.get(
      "preview_theme_id",
    );
    if (previewTheme) url.searchParams.set("preview_theme_id", previewTheme);
    try {
      const response = await fetch(url, { credentials: "same-origin", signal });
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
      );
      const results = html.querySelector("[data-blog-related-results]");
      if (!results || signal.aborted) return;
      const ids = new Set<string>();
      const cards = Array.from(results.children).filter((card) => {
        const id = (card as HTMLElement).dataset.relatedArticleId;
        if (!id || id === this.dataset.articleId || ids.has(id)) return false;
        ids.add(id);
        return true;
      });
      list.replaceChildren(...cards.slice(0, 3));
      aside.hidden = list.children.length === 0;
    } catch {
      // 通信失敗・セクション再読み込み時はLiquidの初期表示を維持する。
    }
  }
}

export function initBlogArticle(): void {
  if (!customElements.get("farmage-blog-article")) {
    customElements.define("farmage-blog-article", FarmageBlogArticle);
  }
}
